import { createSelector, createSlice } from "@reduxjs/toolkit";

import { setDeep } from "Libs/objectAccess";
import logger from "Libs/logger";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";
import { getNewBillingSystemError } from "Hooks/billingSystem/getNewBillingSystemError";

import { getProfileProjects } from "Api/billingSystem/profile/getProfileProjects.request";
import {
  getProfileEstimationsThunk,
  selectProfileEstimation
} from "./profileEstimation";

import type {
  EstimationLineItem,
  EstimationProject
} from "Api/billingSystem/types";
import type { RootState } from "Store/configureStore";

// Types
export type ProfileProject = {
  id: string;
  name: string;
  region: string;
  organizationId: string;
  organizationName: string;
  projectedTotal: string;
  resourceTotal: string;
  usageTotal: string;
  discountTotal: string;
  discounts: EstimationLineItem[];
  planTotal: string;
  total: string;
  hasEstimateError?: boolean;
};

// Thunk
export const getProfileProjectsThunk = createAppAsyncThunk(
  "billingSystem/profileProjects/get",
  async (
    { billingProfileId }: { billingProfileId: string },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      // The project list provides the name + organization mapping; the profile
      // estimate already carries every project's totals in `projects[]`, so we
      // join against it instead of making one estimate request per project.
      const response = await getProfileProjects(billingProfileId);
      const projects = response.items;

      // Reuse the estimate the cost page already loads; only fetch it if it
      // isn't cached yet.
      let estimation = selectProfileEstimation(getState(), billingProfileId);
      if (!estimation) {
        estimation = await dispatch(
          getProfileEstimationsThunk({ billingProfileId })
        )
          .unwrap()
          .catch(() => undefined);
      }

      const estimateByProjectId: Record<string, EstimationProject> = {};
      (estimation?.projects ?? []).forEach(projectEstimate => {
        estimateByProjectId[projectEstimate.project_id] = projectEstimate;
      });

      const projectsWithEstimates: ProfileProject[] = projects.map(project => {
        const estimate = estimateByProjectId[project.id];

        // A project missing from the estimate (e.g. brand new, or the estimate
        // failed to load) renders without totals — same as the old per-project
        // failure case.
        if (!estimate) {
          return {
            id: project.id,
            name: project.title,
            region: project.region,
            organizationId: project.organization_id,
            organizationName: project.organization_id,
            projectedTotal: "",
            resourceTotal: "",
            usageTotal: "",
            discountTotal: "",
            discounts: [],
            planTotal: "",
            total: "",
            hasEstimateError: true
          };
        }

        return {
          id: project.id,
          name: project.title,
          region: project.region,
          organizationId: project.organization_id,
          organizationName: project.organization_id,
          projectedTotal: estimate.total.formatted,
          resourceTotal: estimate.resource_total.formatted,
          usageTotal: estimate.usage_total.formatted,
          discountTotal: estimate.discount_total.formatted,
          discounts: estimate.discounts ?? [],
          planTotal: estimate.plan.formatted,
          total: estimate.total.formatted,
          hasEstimateError: false
        };
      });

      return {
        billingProfileId,
        projects: projectsWithEstimates
      };
    } catch (err) {
      logger(err, {
        action: "get_profile_projects",
        billingProfileId
      });
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  }
);

// State type
type ProfileProjectsState = {
  dataByProfileId: Record<
    string,
    {
      projects: ProfileProject[];
      loading: boolean;
      error?: string;
    }
  >;
};

const initialState: ProfileProjectsState = {
  dataByProfileId: {}
};

// Slice
const profileProjectsSlice = createSlice({
  name: "profileProjects",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getProfileProjectsThunk.pending, (state, { meta }) => {
        const { billingProfileId } = meta.arg;
        setDeep(state, ["dataByProfileId", billingProfileId, "loading"], true);
        setDeep(
          state,
          ["dataByProfileId", billingProfileId, "error"],
          undefined
        );
      })
      .addCase(
        getProfileProjectsThunk.fulfilled,
        (state, { meta, payload }) => {
          const { billingProfileId } = meta.arg;
          setDeep(
            state,
            ["dataByProfileId", billingProfileId, "loading"],
            false
          );
          setDeep(
            state,
            ["dataByProfileId", billingProfileId, "projects"],
            payload.projects
          );
        }
      )
      .addCase(getProfileProjectsThunk.rejected, (state, { meta, payload }) => {
        const { billingProfileId } = meta.arg;
        setDeep(state, ["dataByProfileId", billingProfileId, "loading"], false);
        setDeep(
          state,
          ["dataByProfileId", billingProfileId, "error"],
          payload?.error
        );
      });
  }
});

export default profileProjectsSlice.reducer;

// Selectors
const profileProjectsStateSelector = (state: RootState) =>
  state.profileProjects;

export const selectProfileEstimatesProjects = createSelector(
  profileProjectsStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) =>
    state.dataByProfileId[billingProfileId]?.projects ?? []
);

export const selectProfileEstimatesProjectsLoading = createSelector(
  profileProjectsStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) =>
    state.dataByProfileId[billingProfileId]?.loading ?? false
);

export const selectProfileEstimatesProjectsLoaded = createSelector(
  profileProjectsStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) => billingProfileId in state.dataByProfileId
);

export const selectProfileEstimatesProjectsError = createSelector(
  profileProjectsStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) => state.dataByProfileId[billingProfileId]?.error
);

export const selectProfileEstimatesProjectsWithEstimateErrors = createSelector(
  selectProfileEstimatesProjects,
  projects => projects.filter(project => project.hasEstimateError)
);

export const selectHasProfileProjectsWithEstimateErrors = createSelector(
  selectProfileEstimatesProjectsWithEstimateErrors,
  projectsWithErrors => projectsWithErrors.length > 0
);
